import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CatalogModal from '../components/CatalogModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import { apiService } from '../services/api';
import {
  fetchCatalogs,
  fetchCatalogStats,
  createCatalog,
  updateCatalog,
  setActiveCatalog,
  deleteCatalog,
  setSelectedCatalog,
  exchangeCatalogPermissionCode,
} from '../store/slices/catalogsSlice';
import './Catalog.css';

// Meta OAuth dialog to (re)grant catalog/WhatsApp management permissions
const FB_CATALOG_PERMISSION_URL =
  'https://www.facebook.com/v20.0/dialog/oauth?client_id=1569886665141190&redirect_uri=https://waba.hawktrion.com/catalog&scope=catalog_management,whatsapp_business_management,whatsapp_business_manage_events,whatsapp_business_messaging&response_type=code';

// Detects the "(#100) Missing Permission" OAuthException Meta returns on catalog creation
const isMissingPermissionError = (err) => {
  const metaError = err?.details?.error;
  return metaError?.code === 100 || /missing permission/i.test(metaError?.message || '');
};

const Catalog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { items: catalogs, selectedCatalog, stats, loading, error } = useSelector(
    (state) => state.catalogs
  );

  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sendCatalogModal, setSendCatalogModal] = useState({ open: false, catalog: null });
  const [sendForm, setSendForm] = useState({ bodyText: '', thumbnailProductRetailerId: '', sendMode: 'catalog' });
  const [sending, setSending] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  // ── Load data on mount ────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchCatalogs());
    dispatch(fetchCatalogStats());
  }, [dispatch]);

  // Exchange the ?code=... returned by the Meta permission dialog for an access token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code) return;

    
    dispatch(exchangeCatalogPermissionCode(code))
      .unwrap()
      .then(() => {
        toast.success('Permission granted! You can now create the catalog.', { position: 'bottom-right' });
      })
      .catch((err) => {
        toast.error(err?.error || err?.message || 'Failed to generate access token', { position: 'bottom-right' });
      })
      .finally(() => {
        // Strip ?code=...&#_=_ from the URL so the exchange doesn't repeat on refresh
        navigate('/catalog', { replace: true });
      });
  }, [dispatch, navigate]);

  // Re-fetch when filters change
  useEffect(() => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (searchTerm.trim()) params.search = searchTerm.trim();
    dispatch(fetchCatalogs(params));
  }, [searchTerm, statusFilter, dispatch]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCreateCatalog = async (catalogData) => {
    const payload = {
      name: catalogData.name,
      vertical: catalogData.vertical,
      description: catalogData.description || `${catalogData.vertical} catalog`,
      productCount: catalogData.product_count || 0,
      feedCount: catalogData.feed_count || 1,
      defaultImageUrl: catalogData.default_image_url || '',
      fallbackImageUrls: catalogData.fallback_image_url || [],
      isCatalogSegment: catalogData.is_catalog_segment || false,
      daDisplaySettings: {
        carouselAd: {
          transformationType: catalogData.da_display_settings?.carousel_ad?.transformation_type || 'none',
        },
        singleAd: {
          transformationType: catalogData.da_display_settings?.single_ad?.transformation_type || 'none',
        },
      },
    };

    try {
      const result = await dispatch(createCatalog(payload)).unwrap();
      toast.success(result.message || `Catalog "${catalogData.name}" created!`, {
        position: 'bottom-right',
        autoClose: 3000,
      });
      dispatch(fetchCatalogStats());
      setIsCatalogModalOpen(false);
    } catch (err) {
      if (isMissingPermissionError(err)) {
        setIsPermissionModalOpen(true);
      } else {
        const msg = err?.error || err?.message || 'Failed to create catalog';
        toast.error(msg, { position: 'bottom-right' });
      }
      throw err;
    }
  };

  const handleGrantPermission = () => {
    window.open(FB_CATALOG_PERMISSION_URL, '_blank', 'noopener,noreferrer');
    setIsPermissionModalOpen(false);
  };

  const handleEditCatalog = async (catalogData) => {
    const payload = {
      name: catalogData.name,
      vertical: catalogData.vertical,
      description: catalogData.description || '',
      productCount: catalogData.product_count || 0,
      feedCount: catalogData.feed_count || 1,
      defaultImageUrl: catalogData.default_image_url || '',
      fallbackImageUrls: catalogData.fallback_image_url || [],
      isCatalogSegment: catalogData.is_catalog_segment || false,
      daDisplaySettings: {
        carouselAd: {
          transformationType: catalogData.da_display_settings?.carousel_ad?.transformation_type || 'none',
        },
        singleAd: {
          transformationType: catalogData.da_display_settings?.single_ad?.transformation_type || 'none',
        },
      },
    };

    try {
      const result = await dispatch(updateCatalog({ id: editingCatalog._id, data: payload })).unwrap();
      toast.success(result.message || 'Catalog updated!', { position: 'bottom-right', autoClose: 3000 });
      setEditingCatalog(null);
      setIsCatalogModalOpen(false);
    } catch (err) {
      const msg = err?.message || 'Failed to update catalog';
      toast.error(msg, { position: 'bottom-right' });
      throw err;
    }
  };

  const handleSetActive = async (catalog) => {
    try {
      await dispatch(setActiveCatalog(catalog.metaCatalogId)).unwrap();
      localStorage.setItem('activeCatalogId', catalog.metaCatalogId);
      toast.success(`"${catalog.name}" is now the active catalog`, {
        position: 'bottom-right',
        autoClose: 2000,
      });
      dispatch(fetchCatalogStats());
    } catch (err) {
      toast.error(err?.message || 'Failed to set active catalog', { position: 'bottom-right' });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(deleteCatalog(deleteTarget._id)).unwrap();
      toast.success(`Catalog "${deleteTarget.name}" deleted`, { position: 'bottom-right' });
      if (selectedCatalog?._id === deleteTarget._id) dispatch(setSelectedCatalog(null));
      dispatch(fetchCatalogStats());
    } catch (err) {
      toast.error(err?.message || 'Failed to delete catalog', { position: 'bottom-right' });
    } finally {
      setDeleteTarget(null);
      setIsDeleteModalOpen(false);
    }
  };

  const handleViewProducts = (catalog) => {
    localStorage.setItem('selectedCatalogForView', catalog.metaCatalogId);
    navigate(`/products/${catalog.metaCatalogId}`);
  };

  const openEditModal = (e, catalog) => {
    e.stopPropagation();
    setEditingCatalog(catalog);
    setIsCatalogModalOpen(true);
  };

  const openDeleteModal = (e, catalog) => {
    e.stopPropagation();
    setDeleteTarget(catalog);
    setIsDeleteModalOpen(true);
  };

  const openSendModal = async (e, catalog) => {
    e.stopPropagation();
    setSendForm({ bodyText: '', thumbnailProductRetailerId: '', sendMode: 'catalog' });
    setSelectedCustomers([]);
    setCustomerSearch('');
    setSendCatalogModal({ open: true, catalog });
    setCustomersLoading(true);
    try {
      const res = await apiService.customers.getAll({ limit: 200 });
      const list = res?.data?.data || res?.data?.customers || res?.data || [];
      setCustomers(Array.isArray(list) ? list : []);
    } catch {
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
    }
  };

  const toggleCustomer = (customer) => {
    setSelectedCustomers((prev) =>
      prev.some((c) => c._id === customer._id)
        ? prev.filter((c) => c._id !== customer._id)
        : [...prev, customer]
    );
  };

  const toggleAllCustomers = () => {
    const visible = customers.filter((c) =>
      !customerSearch.trim() ||
      (c.name || '').toLowerCase().includes(customerSearch.toLowerCase()) ||
      (c.phoneNumber || '').includes(customerSearch)
    );
    const allSelected = visible.every((c) => selectedCustomers.some((s) => s._id === c._id));
    if (allSelected) {
      setSelectedCustomers((prev) => prev.filter((s) => !visible.some((v) => v._id === s._id)));
    } else {
      setSelectedCustomers((prev) => {
        const existing = prev.map((p) => p._id);
        return [...prev, ...visible.filter((v) => !existing.includes(v._id))];
      });
    }
  };

  const handleSendCatalog = async () => {
    const { catalog } = sendCatalogModal;
    console.log({catalog});
    if (selectedCustomers.length === 0) {
      toast.error('Select at least one recipient', { position: 'bottom-right' });
      return;
    }
    setSending(true);
    let successCount = 0;
    let failCount = 0;
    for (const customer of selectedCustomers) {
      const phone = customer.phoneNumber;
      if (!phone) { failCount++; continue; }
      try {
        if (sendForm.sendMode === 'catalog_template') {
          await apiService.messages.sendCatalogTemplate({
            toPhoneNumber: phone,
            catalogId: catalog.metaCatalogId || catalog._id,
            productRetailerId: sendForm.thumbnailProductRetailerId.trim() || undefined,
          });
        } else if (sendForm.sendMode === 'product_carousel') {
          await apiService.messages.sendProductCarousel({ toPhoneNumber: phone, catalogId: catalog.metaCatalogId || catalog._id });
        } else {
          await apiService.messages.sendCatalog({
            toPhoneNumber: phone,
            catalogId: catalog.metaCatalogId || catalog._id,
            bodyText: sendForm.bodyText.trim() || `Check out our ${catalog.name} catalog!`,
            thumbnailProductRetailerId: sendForm.thumbnailProductRetailerId.trim() || undefined,
          });
        }
        successCount++;
      } catch {
        failCount++;
      }
    }
    setSending(false);
    if (successCount > 0) {
      const modeLabel = sendForm.sendMode === 'catalog_template' ? 'Catalog template' : sendForm.sendMode === 'product_carousel' ? 'Product carousel' : 'Catalog';
      toast.success(`${modeLabel} sent to ${successCount} recipient${successCount > 1 ? 's' : ''}${failCount > 0 ? ` (${failCount} failed)` : ''}`, { position: 'bottom-right', autoClose: 3000 });
      setSendCatalogModal({ open: false, catalog: null });
    } else {
      toast.error('All sends failed. Check phone numbers.', { position: 'bottom-right' });
    }
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="catalog-container">
      <div className="catalog-header">
        <div className="header-content">
          <h1>Catalogs</h1>
          <p>Manage your product catalogs and set the active catalog</p>
        </div>
        <button
          className="btn-create-catalog"
          onClick={() => { setEditingCatalog(null); setIsCatalogModalOpen(true); }}
        >
          + Create Catalog
        </button>
      </div>

      {/* Stats Banner */}
      <div className="catalog-stats-banner">
        <div className="stat-card">
          <div className="stat-value">{stats?.total ?? catalogs.length}</div>
          <div className="stat-label">Total Catalogs</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.totalProducts ?? 0}</div>
          <div className="stat-label">Total Products</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats?.active ?? catalogs.filter((c) => c.status === 'active').length}</div>
          <div className="stat-label">Active Catalogs</div>
        </div>
        <div className="stat-card active-catalog-card">
          <div className="stat-value">
            {stats?.activeCatalog ? stats.activeCatalog.name.substring(0, 12) + (stats.activeCatalog.name.length > 12 ? '…' : '') : 'None'}
          </div>
          <div className="stat-label">Active Catalog</div>
        </div>
      </div>

      {/* Filters */}
      <div className="catalog-filters">
        <input
          className="catalog-search-input"
          type="text"
          placeholder="Search catalogs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="catalog-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="catalog-error-banner">
          {typeof error === 'string' ? error : error.message || 'An error occurred'}
        </div>
      )}

      {/* Catalogs Grid Section */}
      <div className="catalogs-section">
        <div className="section-header">
          <h2>Available Catalogs</h2>
          <p>Click on a catalog to view details. Use "Set Active" to make it the default.</p>
        </div>

        {loading && catalogs.length === 0 ? (
          <div className="catalog-loading">Loading catalogs...</div>
        ) : catalogs.length === 0 ? (
          <div className="catalog-empty">
            <div className="empty-icon">📦</div>
            <p>No catalogs found. Create your first catalog to get started.</p>
          </div>
        ) : (
          <div className="catalogs-grid">
            {catalogs.map((catalog) => (
              <div
                key={catalog._id}
                className={`catalog-card ${catalog.isActive ? 'active' : ''}`}
                onClick={() => dispatch(setSelectedCatalog(catalog))}
              >
                <div className="catalog-card-header">
                  <div className="catalog-image-wrapper">
                    <img
                      src={catalog.defaultImageUrl || '/placeholder.png'}
                      alt={catalog.name}
                      className="catalog-image"
                    />
                  </div>
                  <span className={`catalog-status ${catalog.status}`}>{catalog.status}</span>
                </div>

                <div className="catalog-card-content">
                  <h3 className="catalog-name">{catalog.name}</h3>
                  <p className="catalog-description">{catalog.description}</p>

                  <div className="catalog-info">
                    <div className="info-item">
                      <span className="info-label">Products:</span>
                      <span className="info-value">{catalog.productCount}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Vertical:</span>
                      <span className="info-value">{catalog.vertical}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Created:</span>
                      <span className="info-value">{formatDate(catalog.createdAt)}</span>
                    </div>
                  </div>
                </div>

                <div className="catalog-card-actions">
                  {catalog.isActive ? (
                    <button className="btn-active" disabled>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Active
                    </button>
                  ) : (
                    <button
                      className="btn-set-active-catalog"
                      onClick={(e) => { e.stopPropagation(); handleSetActive(catalog); }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                      Set Active
                    </button>
                  )}
                  <button
                    className="btn-send-catalog"
                    onClick={(e) => openSendModal(e, catalog)}
                    title="Send catalog to user"
                  >
                    📤
                  </button>
                  <button
                    className="btn-edit-catalog"
                    onClick={(e) => openEditModal(e, catalog)}
                    title="Edit catalog"
                  >
                    ✏️
                  </button>
                  <button
                    className="btn-delete-catalog"
                    onClick={(e) => openDeleteModal(e, catalog)}
                    title="Delete catalog"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Catalog Detail Section */}
      <div className="catalog-detail-section">
        <div className="section-header">
          <h2>Catalog Details</h2>
          <p>
            {selectedCatalog
              ? `Details for "${selectedCatalog.name}"`
              : 'Select a catalog to view details'}
          </p>
        </div>

        {selectedCatalog ? (
          <div className="detail-card">
            <div className="detail-grid">
              <div className="detail-field">
                <label>Catalog Name</label>
                <div className="detail-value-large">{selectedCatalog.name}</div>
              </div>

              <div className="detail-field">
                <label>Catalog ID</label>
                <div className="detail-value-monospace">{selectedCatalog._id}</div>
              </div>

              <div className="detail-field full-width">
                <label>Description</label>
                <div className="detail-value-large">{selectedCatalog.description || '—'}</div>
              </div>

              <div className="detail-field">
                <label>Status</label>
                <span className={`status-badge ${selectedCatalog.status}`}>
                  {selectedCatalog.status.toUpperCase()}
                </span>
              </div>

              <div className="detail-field">
                <label>Vertical</label>
                <div className="detail-value-large">{selectedCatalog.vertical}</div>
              </div>

              <div className="detail-field">
                <label>Product Count</label>
                <div className="detail-value-large">{selectedCatalog.productCount} products</div>
              </div>

              <div className="detail-field">
                <label>Feed Count</label>
                <div className="detail-value-large">{selectedCatalog.feedCount}</div>
              </div>

              <div className="detail-field">
                <label>Created Date</label>
                <div className="detail-value-large">{formatDate(selectedCatalog.createdAt)}</div>
              </div>
            </div>

            <div className="detail-actions">
              {selectedCatalog.isActive ? (
                <button className="btn-action btn-active-detail" disabled>
                  ✅ Currently Active
                </button>
              ) : (
                <button
                  className="btn-action btn-set-active-detail"
                  onClick={() => handleSetActive(selectedCatalog)}
                >
                  Set as Active Catalog
                </button>
              )}
              <button
                className="btn-action btn-send-catalog-detail"
                onClick={(e) => openSendModal(e, selectedCatalog)}
              >
                Send Catalog
              </button>
              <button
                className="btn-action btn-view-products"
                onClick={() => handleViewProducts(selectedCatalog)}
              >
                View Products
              </button>
              <button
                className="btn-action btn-edit-detail"
                onClick={(e) => openEditModal(e, selectedCatalog)}
              >
                Edit Catalog
              </button>
              <button
                className="btn-action btn-delete-detail"
                onClick={(e) => openDeleteModal(e, selectedCatalog)}
              >
                Delete Catalog
              </button>
            </div>
          </div>
        ) : (
          <div className="empty-detail">
            <div className="empty-icon">📋</div>
            <p>Select a catalog to view its details</p>
          </div>
        )}
      </div>

      {/* Send Catalog Modal */}
      {sendCatalogModal.open && (() => {
        const filteredCustomers = customers.filter((c) =>
          !customerSearch.trim() ||
          (c.name || '').toLowerCase().includes(customerSearch.toLowerCase()) ||
          (c.phoneNumber || '').includes(customerSearch)
        );
        const allChecked = filteredCustomers.length > 0 &&
          filteredCustomers.every((c) => selectedCustomers.some((s) => s._id === c._id));
        return (
          <div className="modal-overlay" onClick={() => setSendCatalogModal({ open: false, catalog: null })}>
            <div className="modal-content send-catalog-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Send to Users</h2>
                <button className="modal-close-btn" onClick={() => setSendCatalogModal({ open: false, catalog: null })}>✕</button>
              </div>

              <div className="modal-body">
                <p className="send-catalog-subtitle">
                  Catalog: <strong>{sendCatalogModal.catalog?.name}</strong>
                </p>

                {/* Send mode tabs */}
                <div className="send-mode-tabs">
                  <button
                    className={`send-mode-tab ${sendForm.sendMode === 'catalog' ? 'active' : ''}`}
                    onClick={() => setSendForm((f) => ({ ...f, sendMode: 'catalog' }))}
                  >
                    📋 Catalog
                  </button>
                  <button
                    className={`send-mode-tab ${sendForm.sendMode === 'catalog_template' ? 'active' : ''}`}
                    onClick={() => setSendForm((f) => ({ ...f, sendMode: 'catalog_template' }))}
                  >
                    📑 Catalog Template
                  </button>
                  <button
                    className={`send-mode-tab ${sendForm.sendMode === 'product_carousel' ? 'active' : ''}`}
                    onClick={() => setSendForm((f) => ({ ...f, sendMode: 'product_carousel' }))}
                  >
                    🎠 Product Carousel
                  </button>
                </div>

                {sendForm.sendMode === 'catalog' && (
                  <p className="send-mode-hint">Sends an interactive catalog message directly via WhatsApp API.</p>
                )}
                {sendForm.sendMode === 'catalog_template' && (
                  <p className="send-mode-hint">Sends using an approved <strong>CATALOG</strong> template with a catalog button.</p>
                )}
                {sendForm.sendMode === 'product_carousel' && (
                  <p className="send-mode-hint">Sends an approved <strong>PRODUCT_CAROUSEL</strong> template with active products as carousel cards.</p>
                )}

                {/* Customer multi-select */}
                <div className="form-group">
                  <div className="customer-list-header">
                    <label>
                      Recipients <span className="required">*</span>
                      {selectedCustomers.length > 0 && (
                        <span className="selected-badge">{selectedCustomers.length} selected</span>
                      )}
                    </label>
                    <input
                      type="text"
                      className="customer-search-input"
                      placeholder="Search by name or phone…"
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                    />
                  </div>

                  <div className="customer-list-box">
                    {customersLoading ? (
                      <div className="customer-list-loading">Loading customers…</div>
                    ) : customers.length === 0 ? (
                      <div className="customer-list-empty">No customers found</div>
                    ) : (
                      <>
                        {/* Select all row */}
                        <div className="customer-list-item customer-list-select-all" onClick={toggleAllCustomers}>
                          <input
                            type="checkbox"
                            checked={allChecked}
                            onChange={toggleAllCustomers}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="customer-name">Select all ({filteredCustomers.length})</span>
                        </div>

                        {filteredCustomers.length === 0 ? (
                          <div className="customer-list-empty">No matches for "{customerSearch}"</div>
                        ) : (
                          filteredCustomers.map((customer) => {
                            const checked = selectedCustomers.some((s) => s._id === customer._id);
                            return (
                              <div
                                key={customer._id}
                                className={`customer-list-item ${checked ? 'checked' : ''}`}
                                onClick={() => toggleCustomer(customer)}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleCustomer(customer)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div className="customer-avatar">
                                  {(customer.name || customer.phoneNumber || '?')[0].toUpperCase()}
                                </div>
                                <div className="customer-info">
                                  <span className="customer-name">{customer.name || '—'}</span>
                                  <span className="customer-phone">{customer.phoneNumber}</span>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </>
                    )}
                  </div>
                </div>

                {sendForm.sendMode === 'catalog' && (
                  <div className="form-group">
                    <label>Message Body</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      placeholder={`Check out our ${sendCatalogModal.catalog?.name} catalog!`}
                      value={sendForm.bodyText}
                      onChange={(e) => setSendForm((f) => ({ ...f, bodyText: e.target.value }))}
                    />
                  </div>
                )}

                {(sendForm.sendMode === 'catalog' || sendForm.sendMode === 'catalog_template') && (
                  <div className="form-group">
                    <label>
                      {sendForm.sendMode === 'catalog_template' ? 'Product Retailer ID' : 'Thumbnail Product Retailer ID'}
                      {' '}<span className="optional">(optional)</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Product retailer ID for thumbnail"
                      value={sendForm.thumbnailProductRetailerId}
                      onChange={(e) => setSendForm((f) => ({ ...f, thumbnailProductRetailerId: e.target.value }))}
                    />
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn-cancel"
                  onClick={() => setSendCatalogModal({ open: false, catalog: null })}
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  className="btn-send-confirm"
                  onClick={handleSendCatalog}
                  disabled={sending || selectedCustomers.length === 0}
                >
                  {sending
                    ? `Sending… (${selectedCustomers.length})`
                    : `${sendForm.sendMode === 'product_carousel' ? 'Send Carousel' : 'Send Catalog'} to ${selectedCustomers.length || 0} recipient${selectedCustomers.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Create / Edit Catalog Modal */}
      <CatalogModal
        isOpen={isCatalogModalOpen}
        onClose={() => { setIsCatalogModalOpen(false); setEditingCatalog(null); }}
        onConfirm={editingCatalog ? handleEditCatalog : handleCreateCatalog}
        editingCatalog={editingCatalog}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleDeleteConfirm}
        itemName={deleteTarget?.name || 'this catalog'}
        itemType="catalog"
      />

      {/* Missing Permission Prompt */}
      {isPermissionModalOpen && (
        <div className="modal-overlay" onClick={() => setIsPermissionModalOpen(false)}>
          <div className="delete-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="16"></line>
                <line x1="8" y1="12" x2="16" y2="12"></line>
              </svg>
            </div>
            <h2>Permission Required</h2>
            <p>
              Meta rejected the catalog creation request with <strong>Missing Permission</strong>.
              You need to grant <strong>catalog_management</strong> and <strong>whatsapp_business_management</strong>{' '}
              permissions to continue.
            </p>
            <div className="delete-modal-actions">
              <button className="btn-cancel" onClick={() => setIsPermissionModalOpen(false)}>
                Cancel
              </button>
              <button className="btn-delete" onClick={handleGrantPermission}>
                Grant Permission
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalog;

